
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  UserListModel,
  UserListPageSearchModel,
  UserListSearchModel,
  UserAppUpdateModel,
  UserAppDeleteModel
} from '@/model/userModel'
import {
  pageSearchUserList,
  updateAppUser,
  deleteByUserId
} from '@/apis/user'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const searchStruct = [
      { prop: 'userId', label: t('userList.userId'), placeholder: t('userList.userIdP'), component: 'el-input', type: 'text', model: null },
      { prop: 'nickName', label: t('userList.nickName'), placeholder: t('userList.nickNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'mobile', label: t('userList.mobile'), placeholder: t('userList.mobileP'), component: 'el-input', type: 'text', model: null },
      { prop: 'email', label: t('userList.email'), placeholder: t('userList.emailP'), component: 'el-input', type: 'text', model: null }
    ]

    const updateStruct = [
      { prop: 'userId', label: t('userList.userId'), placeholder: t('userList.userIdP'), component: 'el-input', type: 'text', model: null, readonly: true },
      { prop: 'nickName', label: t('userList.nickName'), placeholder: t('userList.nickNameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'email', label: t('userList.email'), placeholder: t('userList.emailP'), component: 'el-input', type: 'text', model: null }
    ]

    const userListRole = {
      appName: [{ required: true, message: t('userList.userId').concat(t('common.isRequired')), trigger: 'blur' }],
      appDescription: [{ required: true, message: t('userList.nickName').concat(t('common.isRequired')), trigger: 'blur' }],
      minVersion: [{ required: true, message: t('userList.email').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [
      { prop: 'userId', label: t('userList.userId'), sortable: 'custom' },
      // { prop: 'userName', label: t('userList.userName'), sortable: 'custom' },
      // { prop: 'userType', label: t('userList.userType'), sortable: 'custom' },
      { prop: 'nickName', label: t('userList.nickName'), sortable: 'custom' },
      { prop: 'avatar', label: t('userList.avatar'), sortable: 'custom' },
      { prop: 'mobile', label: t('userList.mobile'), sortable: 'custom' },
      { prop: 'email', label: t('userList.email'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: UserListModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: UserListModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as UserListSearchModel,
      tableData: Array<UserListModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'userList.edit',
        create: 'userList.create'
      },
      dialogFormVisible: false,
      userListRole: userListRole,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as UserAppUpdateModel,
      uploadPackageModel: {} as any,
      packageAddress: '',
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: UserListPageSearchModel = {
          userId: dataMap.searchFormModel.userId,
          userName: null,
          mobile: dataMap.searchFormModel.mobile,
          email: dataMap.searchFormModel.email,
          nickName: dataMap.searchFormModel.nickName,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchUserList(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async updateSubmit() {
        const updateData: UserAppUpdateModel = {
          userId: dataMap.updateModel.userId,
          nickName: dataMap.updateModel.nickName,
          email: dataMap.updateModel.email
        }
        const result = await updateAppUser(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateReset() {
        console.log('update reset')
      },
      async deleteData(id: number) {
        const delData: UserAppDeleteModel = {
          userId: id
        }
        const result = await deleteByUserId(delData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handleEdit = (row?: UserListModel) => {
      if (row) {
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.userId = row.userId
        dataMap.updateModel.nickName = row.nickName
        dataMap.updateModel.email = row.email
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleEdit,
      searchFormRef,
      handleDelete,
      ...toRefs(dataMap)
    }
  }
})
